/*
 * Copyright 2019 Hippo B.V. (http://www.onehippo.com)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { AlertService } from '@accounts/services/alert.service';
import { NotificationService } from '@accounts/services/notification.service';
import {
  AlertObj,
  NotificationDetails,
  SpinnerDetails,
} from '@accounts/site-alerts/site-alerts.interface';
import { DOCUMENT, Location } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Meta, MetaDefinition, Title } from '@angular/platform-browser';
import {
  ActivatedRoute,
  NavigationEnd,
  ResolveStart,
  Router,
} from '@angular/router';
import { Configuration, Page, Reference } from '@bloomreach/spa-sdk';
import {
  AnalyticsService,
  CustomDimensions,
  DialogConfig,
  DialogService,
  NavigationService,
  PrimaryNavigationService,
  WindowService,
} from '@frk/eds-components';
import { CartHandlerService } from '@literature/services/cart-handler.service';
import { WINDOW } from '@ng-web-apis/common';
import {
  PageConfig,
  PageContainerService,
} from '@pages/services/page-container.service';
import {
  AsyncContentManagerService,
  ClassExclusionService,
  IsLoginUser,
  IUserProfile,
  PageMonitor,
  ProfileService,
  SchemaService,
  SignInService,
  SiteConfigService,
  StorageService,
  WidenService,
} from '@services';
import { WindowScrollService } from '@shared/services/window-scroll.service';
import {
  LeavingSite,
  OutageContent,
  SegmentId,
  UtmCampainDetails,
} from '@types';
import {
  ACCOUNTS_DASHBOARD,
  LOGOUT_TRUE,
  MY_CLIENTS,
  NEVER,
  NEVER_BTN,
  NOTNOW_BTN,
  OPEN_BTN,
  RECENT_TRANSACTIONS,
  STATEMENTS,
  TAX_DOCUMENTS,
  UPGRADE_BTN,
  UPGRADE_FLAG,
} from '@utils/app.constants';
import { PageThemeService } from '../services/page-theme.service';
import { LabelLoader } from '@utils/label-loader';
import { LabelCollection, Labels } from '@utils/labels';
import { Logger } from '@utils/logger';
import { removePTags } from '@utils/text/string-utils';
import { checkLink } from '@utils/link-utils';
import get from 'lodash/get';
import { NgxSpinnerService } from 'ngx-spinner';
import { combineLatest, Subject, timer } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  startWith,
  take,
  takeUntil,
} from 'rxjs/operators';
import { ComponentMapping } from '@services/module-loader.config';

const logger = Logger.getLogger('PageContainerComponent');

declare global {
  interface Window {
    appInitChannelConfig: { basepath: string; defaultSegment?: SegmentId };
    OneTrust: any;
    MktoForms2: any;
  }
}

/**
 * Page Container
 */
@Component({
  selector: 'page-container',
  templateUrl: './page-container.component.html',
  styleUrls: ['./page-container.component.scss'],
  providers: [LabelLoader],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageContainerComponent
  implements OnDestroy, OnInit, AfterViewInit {
  /**
   * Absolute url regex
   */
  absoluteUrl = /^(http|https)?:?\/\//i;

  /**
   * External link dialog config
   */
  dialogConfig: DialogConfig;
  dialogConfigCustom: DialogConfig;

  /**
   * Configuration
   */
  configuration: Configuration;

  /**
   * Bloomreach page object
   */
  page: Page;

  /**
   * Bloomreach Component Mapping
   */
  mapping: ComponentMapping;

  /**
   * Layout name is used to determine which component to load
   */
  layoutName: string;

  pageType: string;
  /**
   * used to remove subscriptions when component is destroyed
   */
  private unsubscribe$: Subject<void> = new Subject<void>();
  private unsubscribeTimer$: Subject<void> = new Subject<void>();

  /**
   * Requested url
   */
  private requestUrl: string;

  private percentageCounter: number;

  private fileConversion: boolean;

  isTemit: boolean;

  private layoutsWithCustomPageViewEvent = ['product-detail', 'insight'];

  notificationDetails: NotificationDetails;
  outageDetails: NotificationDetails;

  utmUrl: string;

  utmValue: Partial<UtmCampainDetails>;

  urlHashValue: string;

  private oneMinuteSpentOnPage: boolean;
  // Map to track event has been fired for the specific scroll
  private scrollEventFired: Map<string, boolean> = new Map(
    Object.entries({ 50: false, 75: false })
  );
  /**
   * Spinner Details
   */
  spinnerDetails: SpinnerDetails;

  segmentOk = false;

  /**
   * Constructor
   */
  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private analytics: AnalyticsService,
    private router: Router,
    private loc: Location,
    private spinner: NgxSpinnerService,
    private dialogService: DialogService,
    private navService: NavigationService,
    private meta: Meta,
    private title: Title,
    @Inject(DOCUMENT) private documentRef: Document,
    @Inject(WINDOW) readonly windowRef: Window,
    private activatedRoute: ActivatedRoute,
    private alertService: AlertService,
    private pageContainerService: PageContainerService,
    private notificationService: NotificationService,
    private primaryNavService: PrimaryNavigationService,
    private profileService: ProfileService,
    private siteConfigService: SiteConfigService,
    private classExclusionService: ClassExclusionService,
    private cartService: CartHandlerService,
    private storageService: StorageService,
    private widenService: WidenService,
    private schemaService: SchemaService,
    private pageThemeService: PageThemeService,
    private signInService: SignInService,
    private windowService: WindowService,
    private acm: AsyncContentManagerService,
    private scrollService: WindowScrollService
  ) {
    this.requestUrl = this.loc.path();

    this.isTemit = this.siteConfigService.isTemit();

    this.pageContainerService.page$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((pageConfig: PageConfig) => {
        this.mapping = pageConfig.mapping;
        this.page = pageConfig.page;
        this.segmentOk = this.page.isPreview() || pageConfig.segmentOk;
        if (this.page.isPreview()) {
          // remove one trust consent form in preview
          this.documentRef.getElementById('onetrust-consent-sdk')?.remove();
        }
        this.configuration = pageConfig.configuration;

        this.acm.registerComponents(this.page);

        // Trigger Page view
        this.layoutName = get(
          this.page,
          'model.page._meta.params.layout',
          'twocols'
        );

        this.pageType = get(this.page, 'model.page._meta.params.pageType');

        if (!this.dialogConfig) {
          this.initDialogConfig();
        }

        this.pageThemeService.setPageTheme(this.page);

        this.setMetaData(this.page);
        // Schema Template
        this.setJsonLdSchema();
        this.spinner.hide();
        this.primaryNavService.closeMegaMenu();
        this.changeDetectorRef.detectChanges();
        setTimeout(() => {
          (this.windowRef as any).prerenderReady = true;
          logger.debug('Setting prerenderReady=true');
        }, 10000);

        /**
         * Set active primary navigation menu item
         * Adding this.windowRef.location.search to compare query parameters along with URL if present
         * e.g. http://<environment>/investments/options/mutual-funds?filter=SHARE_CLASS_NAME:Class%20A;
         */
        this.primaryNavService.setActivePrimaryNav(
          this.windowRef.location.pathname + this.windowRef.location.search
        );

        /**
         * If hash is present in URL
         * -- create scroll request to be actioned once data+images have loaded
         */
        if (this.urlHashValue && this.windowRef.location.hash !== '') {
          logger.debug('hash value', this.urlHashValue);
          this.scrollService.scrollToAnchor(this.urlHashValue);
        }
        this.setOutage();
      });

    // Nav start
    // WARNING: it's important this uses ResolveStart instead of NavigationStart, so canDeactivate Guard runs before it
    this.router.events
      .pipe(
        takeUntil(this.unsubscribe$),
        filter((event) => event instanceof ResolveStart),
        // remove fragment from url, so we can filter out this change
        map((event: ResolveStart): string => event.url.split('#')[0]),
        // need startWith() because this subscribes after initial NavigationStart event
        // NB: startWith() isn't deprecated if your IDE complains:
        // https://stackoverflow.com/questions/56571406/is-the-startwith-operator-in-rxjs-really-deprecated
        startWith(this.router.routerState?.snapshot.url.split('#')[0]),
        map((url: string) => (url ? this.removeParam(url, 'query') : '')),
        map((url: string) => (url ? this.removeParam(url, 'queryType') : '')),
        // distinctUntilChanged() means this only fires for route changes - not fragment changes
        distinctUntilChanged()
      )
      .subscribe((url) => {
        PageMonitor.getMonitor().newPage(url);
        logger.debug('Show Spinner', url);
        // Show spinner
        this.spinner.show();
        // Hide the Logout Alert for next page redirect
        this.alertService.showAlert = false;
      });

    // Observe navigation end and get params for utm Campaign tracking.
    this.router.events
      .pipe(
        takeUntil(this.unsubscribe$),
        filter((event) => event instanceof NavigationEnd)
      )
      .subscribe((event: NavigationEnd) => {
        // get the hash value from url
        if (event.url.includes('#')) {
          this.urlHashValue = event.url.split('#')[1];
        }
        if (event.url.includes('utm_')) {
          this.utmUrl = event.url;
        }
      });

    combineLatest([
      this.pageContainerService.page$.pipe(
        map((pageConfig) => pageConfig.page.getUrl()),
        distinctUntilChanged()
      ),
      this.profileService.getUserProfile$(),
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([page, profile]) => {
        logger.debug('Logger Profile for analytics', page, profile);
        if (
          // we are using pagetype as a switch to filter pageview Event.
          // product-detail.component and article.component fire their own pageview events.
          !this.layoutsWithCustomPageViewEvent.includes(this.layoutName) &&
          !this.layoutsWithCustomPageViewEvent.includes(this.pageType)
        ) {
          this.triggerPageView();
        }
      });

    // Observe navigation end
    this.router.events
      .pipe(
        takeUntil(this.unsubscribe$),
        filter((event) => event instanceof NavigationEnd),
        // removes # fragment so we can ignore in-page links
        map((event: NavigationEnd): string => event.url.split('#')[0]),
        // ignores parameter 'query' which is changed from search component
        // we don't need to load new page when query string is changing when search term is changed
        map((url: string) => (url ? this.removeParam(url, 'query') : '')),
        map((url: string) => (url ? this.removeParam(url, 'queryType') : '')),
        distinctUntilChanged()
      )
      .subscribe((url: string) => {
        logger.debug('NavigationEnd', url);
        this.pageContainerService.newPage(url);

        if (!this.windowService.isPageTop()) {
          this.scrollService.scrollToTop();
        }
      });

    // Get alerts and send to alert components
    this.alertService
      .getAlert$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((notificationData: NotificationDetails) => {
        this.notificationDetails = notificationData;
      });
    this.alertService
      .getOutage$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((notificationData: NotificationDetails) => {
        this.outageDetails = notificationData;
      });
    this.signInService
      .getSpinner$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((spinnerDetails: SpinnerDetails) => {
        this.spinnerDetails = spinnerDetails;
        if (this.spinnerDetails.spinnerStatus) {
          this.spinner.show(this.spinnerDetails.spinnerName);
        } else {
          this.spinner.hide(this.spinnerDetails.spinnerName);
        }
      });
  }

  /**
   * Page loaded
   */
  ngAfterViewInit() {
    this.percentageCounter = 0;
    this.fileConversion = false;
    this.startCountdownForMinute();
    if (this.utmUrl !== undefined) {
      this.getUtmUrl();
    }

    // Track Window Scroll Analytics (Throttled)
    // TODO: do we really need to run this 20 times a second?
    this.windowService
      .getScrollObs$(50) // Throttle time 50ms to track if a user scrolls very fast
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => this.trackScrollAnalytics());
  }

  /**
   * On initialisation
   */
  ngOnInit() {
    this.replaceRouteState();

    // Init the dialog for external links
    this.initExternalDialog();
  }

  private setJsonLdSchema() {
    const url = this.router.url;
    const title = this.page.getComponent()?.getModels()?.pageData?.title;
    this.schemaService.insertSchema(
      SchemaService.websiteSchema(url, title),
      'structured-data'
    );
    // Schema Template for org
    this.schemaService.insertSchema(
      SchemaService.orgSchema(),
      'structured-data-org'
    );
    /*
       Register to Angular navigation events to detect navigating away
    */
    this.router.events.pipe(takeUntil(this.unsubscribe$)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        // update the json-ld tag in head
        this.schemaService.insertSchema(
          SchemaService.websiteSchema(url, title),
          'structured-data'
        );
      }
    });
  }

  /**
   * On component destroyed
   * Removes any remaining subscriptions
   */
  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.unsubscribeFromTimer();
  }

  private unsubscribeFromTimer() {
    this.unsubscribeTimer$.next();
    this.unsubscribeTimer$.complete();
  }

  // get URLprams for utm Campaign tracking
  getUtmUrl() {
    const utmTags = [];
    let hash = [];
    const hashes = this.utmUrl.slice(this.utmUrl.indexOf('?') + 1).split('&');
    hashes.forEach((item) => {
      hash = item.split('=');
      utmTags.push([...hash]);
    });
    this.utmValue = Object.fromEntries(utmTags);
  }

  /**
   * Replaces the route state in case of bypass or logout route
   */
  replaceRouteState() {
    const url = this.router.url;
    // TODO: replace with Angular query param map: https://angular.io/guide/router-tutorial-toh#query-parameters
    const urlSplit = url?.split('?');
    if (url && urlSplit.length >= 2 && urlSplit[1].includes(LOGOUT_TRUE)) {
      // Clear the literature cart details in session time out
      this.cartService.emptyCartStorage();
      // register alerts
      this.activatedRoute.queryParams.subscribe((params: AlertObj) => {
        this.alertService.setAlert(params);
      });
      this.loc.replaceState(urlSplit[0]);
    }
  }

  /**
   * Init External Dialog
   */
  initExternalDialog() {
    // Shared dialog
    if (this.dialogService) {
      // Listen for dialog confirmation
      this.dialogService?.getDialogClosedObs().subscribe((dialog) => {
        if (dialog) {
          if (dialog.id.includes('external-link')) {
            // fire lightboxevent on external-link modal
            this.analytics.trackEvent({
              event: 'lightbox_display',
              action: 'click',
              label: dialog.config.link_url,
            });
            // Open external link in "target" tab
            if (dialog.config.successUrl) {
              if (dialog.config.eventName) {
                const cookieName: string = this.profileService.getToolsTermsCookie(
                  dialog.config
                );
                this.storageService.store(cookieName, true);
              }
              const targetWindow = dialog.config.targetWindow || '_self';
              this.windowRef.open(dialog.config.successUrl, targetWindow);
            }
          }
        }
      });
    }
  }

  private setOutage() {
    const contentRef: Reference = get(
      this.page.getComponent('notifications')?.getModels(),
      'notifications[0]',
      ''
    );
    if (contentRef) {
      const outageContent: OutageContent = this.page
        .getContent(contentRef)
        .getData<OutageContent>();

      if (outageContent) {
        this.alertService.outageDetails = this.getAlertDetails(outageContent);
        this.alertService.setOutage();
      }
    }
  }

  private getAlertDetails(outageContent: OutageContent): NotificationDetails {
    return {
      title: outageContent.title,
      subTitle: outageContent.subTitle,
      notificationType: outageContent.type,
      notificationLabel: removePTags(outageContent.message),
      active: true,
      canClose: outageContent.canClose,
      primaryBtnText: this.getButtonText(outageContent, 'primary'),
      secondaryBtnText: this.getButtonText(outageContent, 'secondary'),
      tertiaryBtnText: this.getButtonText(outageContent, 'tertiary'),
    };
  }

  private getButtonText(content: OutageContent, key: string): string {
    return get(
      content.button.find((b) => b.buttonStyle === key),
      'linkCompound.linkCollection[0].displayText',
      ''
    );
  }

  /**
   * Check if clicked link has tab on the same page
   * -- If tab present on the same page trigger click on that tab
   */
  checkIfTabIsPresentForClickedLink(clickedElm): any {
    // get url in browser
    // get url of clicked element
    // compare request url with target pathname to check if its same page being requested
    // check if it has hash
    if (this.requestUrl === clickedElm?.pathname && clickedElm?.hash) {
      // if base url is same as href and it has hash
      // -- then check if page has tabs with same id
      const tabId = this.documentRef.getElementById(
        clickedElm.hash.replace('#', '')
      );

      // --- if true - trigger click
      if (tabId) {
        tabId.click();
        logger.debug('Tab click triggered', tabId);
      }
    }
  }

  /**
   * Rich text links need handled globally to change them
   * to route correctly.
   *
   */
  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): boolean {
    // One trust hook

    const target: EventTarget = checkLink(event);

    if (target instanceof HTMLAnchorElement) {
      this.checkIfTabIsPresentForClickedLink(target);
    }

    if (
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLButtonElement
    ) {
      const classList: DOMTokenList = target.classList;
      if (
        target?.textContent === MY_CLIENTS ||
        target?.textContent === RECENT_TRANSACTIONS ||
        target?.textContent === STATEMENTS ||
        target?.textContent === TAX_DOCUMENTS ||
        target?.textContent.includes(ACCOUNTS_DASHBOARD)
      ) {
        this.notificationService.checkUpgardeOnClick(event);
      }
      if (target?.textContent === UPGRADE_BTN) {
        this.notificationService.upgrade(UPGRADE_FLAG);
      } else if (target?.textContent === NOTNOW_BTN) {
        this.notificationService.notNow();
      } else if (target?.textContent === NEVER_BTN) {
        this.notificationService.upgrade(NEVER);
      } else if (target?.textContent === OPEN_BTN) {
        this.notificationService.newAccount();
      }
      if (classList.contains('ot-sdk-show-settings')) {
        // Launch preferences
        this.showCookieSettings();
        return false;
      }
      // Handle click event
      // Track analytics
      let linkText: string = target.textContent;
      if (!target.textContent) {
        linkText = this.checkImageAlt(event);
      }
      // We need to track events when button is only eds-ICON
      if (!linkText && target.firstElementChild.tagName === 'EDS-ICON') {
        linkText = target.firstElementChild.getAttribute('type');
      }
      const linkUrl: string =
        target instanceof HTMLAnchorElement ? target.href : '';

      if (classList.value.includes('external-link--')) {
        logger.debug('getting custom content for leaving site');
        this.initDialogConfig(this.setExternalModalCustom(classList));
        this.dialogConfigCustom.successUrl = linkUrl;
        this.dialogConfigCustom.targetWindow = target.getAttribute('target');
        const trackConf: DialogConfig = {
          link_text: target.innerText,
          link_url: linkUrl,
          successUrl: linkUrl,
          targetWindow: this.dialogConfigCustom.targetWindow,
        };
        const cookieName: string = this.profileService.getToolsTermsCookie(
          this.dialogConfigCustom
        );
        this.storageService.retrieve(cookieName, false).then((cookie) => {
          if (!cookie) {
            event.preventDefault();
            this.dialogService.open('external-link-custom', trackConf, target);
          }
        });
      }

      let eventName = 'linkOrButtonClick';

      // Check if the target element is for deleting a filter
      const elemChildNodes = target.children[0];
      if (elemChildNodes) {
        Array.from(elemChildNodes.children).forEach((child) => {
          if (child.className === 'analytics-reset') {
            // sets event as form_filter in analytics when filter is being deleted
            eventName = 'form_filter';
          }
        });
      }

      const resetFilterElement = target.parentNode;
      let resetFlag = false;
      // sets event as form_filter in analytics if filter(s) are being reset
      if (resetFilterElement) {
        Array.from(resetFilterElement.children).forEach((child) => {
          if (child.className.includes('analytics-reset')) {
            // sets event as form_filter in analytics when filter is being deleted
            resetFlag = true;
          }
        });
      }

      // Preventing linkOrButtonClick event from firing on download and removal of chips on filters
      const { analyticsNoEvent } = target.dataset;
      const ckEditor = target.parentNode.parentElement.closest('div.ck-editor');
      if (
        !ckEditor &&
        this.classExclusionService.classExclusion(classList) &&
        !linkUrl.includes('widen.net') &&
        !linkUrl.includes('.pdf') &&
        !(analyticsNoEvent === 'true') &&
        !resetFlag &&
        // Disable Tracking event from Brightcove video player's embedded play/pause buttons
        !(linkText === 'Play' || linkText === 'Pause')
      ) {
        this.analytics.trackEvent({
          event: eventName,
          link_text: linkText,
          link_url: linkUrl,
        });
      }

      // Track analytics events when link with pdf is clicked.
      if (linkUrl.includes('widen.net')) {
        // Set event name to file_download
        this.analytics.trackEvent({
          event: 'file_download',
          link_text: linkText,
          file_name: linkUrl.substring(linkUrl.lastIndexOf('/') + 1),
          link_url: linkUrl,
        });
      }
      // Track analytics events when Email button is clicked.
      if (linkUrl.includes('mailto')) {
        // Set event name to contact_link
        this.analytics.trackEvent({
          event: 'contact_link',
          method: 'email',
          label: linkUrl,
        });
      }
      this.trackFileExtDownload(linkUrl);

      // Handle global click events in a service
      return this.navService.navigate(event);
    }
  }

  /**
   * Listener for window scroll
   * @param event - Event
   */

  // @HostListener('window:scroll', ['$event']) onScroll(event: Event): void {
  private trackScrollAnalytics() {
    const scroll = this.getScrollPercent();
    // Scrolling fires analytics only on scroll down. When counter reach 100 analytics is registered.
    // Runs analytics when page scroll reach 25%
    if (scroll >= 25 && scroll < 50 && this.counterCheck(0)) {
      this.percentageCounter = 25;
      this.analyticScroll([this.percentageCounter]);
    }
    // Runs analytics when page scroll reach 50%
    if (scroll >= 50 && scroll < 75 && this.counterCheck(25)) {
      if (this.percentageCounter === 0) {
        this.analyticScroll([25]);
      }
      this.percentageCounter = 50;
      this.analyticScroll([this.percentageCounter]);
    }
    // Runs analytics when user has spent 1 minute on a page
    // and scrolls to either 50% or 75%, only once.
    if (
      this.oneMinuteSpentOnPage &&
      scroll >= 50 &&
      scroll < 75 &&
      !this.scrollEventFired.get('50')
    ) {
      this.track1MinuteScroll(50);
    }
    if (
      this.oneMinuteSpentOnPage &&
      scroll >= 75 &&
      scroll < 100 &&
      !this.scrollEventFired.get('75')
    ) {
      this.track1MinuteScroll(75);
    }
    // Runs analytics when page scroll reach 75%
    if (scroll >= 75 && scroll < 100 && this.counterCheck(50)) {
      if (this.percentageCounter === 0) {
        this.analyticScroll([25, 50]);
      }
      this.percentageCounter = 75;
      this.analyticScroll([this.percentageCounter]);
    }
    // Runs analytics when page scroll reach 100%
    if (scroll === 100 && this.counterCheck(75)) {
      if (this.percentageCounter === 0) {
        this.analyticScroll([25, 50, 75]);
      }
      this.percentageCounter = 100;
      this.analyticScroll([this.percentageCounter]);
    }
  }

  private track1MinuteScroll(scroll: number) {
    this.analytics.trackEvent({
      event: 'read_content',
      action: `1 Minute and ${scroll}% Scroll`,
    });
    this.scrollEventFired.set('' + scroll, true);
  }
  /**
   * Start a countdown for 1 minute
   */
  private startCountdownForMinute() {
    const timeInMilliSecs = 60000;
    timer(timeInMilliSecs)
      .pipe(takeUntil(this.unsubscribeTimer$))
      .subscribe(
        () => {
          this.oneMinuteSpentOnPage = true;
        },
        () => {},
        () => {
          this.unsubscribeFromTimer();
        }
      );
  }
  /**
   * Track for documents fileConversion
   * @param linkUrl - string with clicked link
   */
  private trackFileExtDownload(linkUrl: string): void {
    // This covers PDF files extensions. It might be extended for other files eg. XLS, XSLS, DOC, DOCX
    if (linkUrl.indexOf('.pdf') !== -1 && !this.fileConversion) {
      // logger.debug('Tracking file_conversion - download');
      this.analytics.trackEvent({
        event: 'file_conversion',
        interaction_type: 'download',
        file_extension: 'pdf',
      });
      this.fileConversion = true;
    }
  }

  /**
   * Counting of scroll percentage
   * @param event - Event
   */
  private getScrollPercent(): number {
    // TODO: pageYOffset is deprecated
    return Math.round(
      ((this.windowRef.innerHeight + this.windowRef.scrollY) * 100) /
        this.documentRef.documentElement.scrollHeight
    );
  }

  /**
   * Checks if $percentageCounter reach required value
   * @param countStart start counter
   */
  private counterCheck(countStart: number): boolean {
    return (
      typeof this.percentageCounter === 'undefined' ||
      this.percentageCounter === 0 ||
      this.percentageCounter === countStart
    );
  }

  /**
   * Track scroll event
   * @param scrollArray - Array with scroll #
   */
  private analyticScroll(scrollArray: number[]): void {
    scrollArray.forEach((scroll) => {
      // logger.debug('Scroll', scroll.toString() + '%');
      return this.analytics.trackEvent({
        event: 'scroll_depth',
        action: scroll.toString() + '%',
      });
    });
  }

  /**
   * Returns alt text for image if clocked element is image
   * @param event - click event
   */
  private checkImageAlt(event: MouseEvent): string {
    if (event.target instanceof HTMLImageElement) {
      return event.target.alt;
    }
  }

  /**
   * Trigger Page View event using the AnalyticsService
   */
  async triggerPageView(): Promise<void> {
    try {
      // Custom dimensions
      const customDimensions: CustomDimensions = await this.createCustomDimensions();

      // Event params
      const eventParams = {
        title: this.page.getComponent()?.getModels()?.pageData?.title,
        url: this.router.url,
      };
      // Pass info to service
      this.analytics.pageViewWithCustomDimensions(
        customDimensions,
        eventParams
      );
    } catch (err) {
      logger.error(err);
    }
  }

  /**
   * Create Custom Dimensions to be sent before any other event
   * information.
   */
  async createCustomDimensions(): Promise<CustomDimensions> {
    try {
      // Read metadata from page model
      const metadataComponent = this.page.getComponent(
        'page-config',
        'pageMetadata'
      );

      const pageMetadata = metadataComponent?.getModels()?.pageMetadata;
      const siteMetadata = metadataComponent?.getModels()?.siteMetadata;

      const profileData: IUserProfile = await this.profileService
        .getUserProfile$()
        .pipe(take(1))
        .toPromise();
      const persistantAnalyticsData = await this.pageContainerService.getPartialDimensionOnLoginStatus(
        profileData?.isLoggedIn,
        profileData?.profileInfo
      );

      return {
        page_location: this.router.url,
        page_title: this.page.getComponent()?.getModels()?.pageData?.title,
        page_type: this.page.getComponent()?.getModels()?.pageData?.pageType,
        language_code: this.page.getComponent()?.getModels()?.pageData
          ?.language,
        role_type_track: profileData.profileInfo.role,
        market_code: this.siteConfigService?.product?.site?.country,
        is_internal_user: String(
          this.profileService.checkIfUserIsInternal(
            profileData?.profileInfo?.userGroup
          )
        ),
        express_number: persistantAnalyticsData?.expressNo,
        user_sys_no: persistantAnalyticsData?.userSysNo,
        dealer_number: profileData?.profileInfo?.dealerNo,
        state_code: profileData?.profileInfo?.state,
        zip: this.profileService.get5DigitZip(profileData?.profileInfo?.zip),
        accounts_assigned: profileData?.profileInfo?.accountsAccess,
        firm_name: profileData?.profileInfo?.firm,
        ost_id: profileData?.profileInfo?.ostId,
        role_selected: profileData?.profileInfo?.role,
        logged_in_status: profileData?.isLoggedIn
          ? IsLoginUser.LOGGEDIN
          : IsLoginUser.LOGGEDOUT,
        riva_id: profileData?.isLoggedIn
          ? profileData?.profileInfo?.rivaPartyId
          : '',
        user_group: profileData?.profileInfo?.userGroup,
        country_code: this.siteConfigService?.product?.site?.country,
        data_service_actor: profileData?.profileInfo?.actor,
        utm_source: this.utmValue?.utm_source,
        utm_medium: this.utmValue?.utm_medium,
        utm_campaign: this.utmValue?.utm_campaign,
        utm_content: this.utmValue?.utm_content,
        subsegment: profileData?.profileInfo?.webExperience,
        web_experience: profileData.profileInfo.webExperience,
      };
    } catch (err) {
      throw err;
    }
  }

  /**
   * Initialise Dialog config
   */
  initDialogConfig(customContent?: string): void {
    const labels = Labels.getLabels(LabelCollection.common);
    const leavingSite: LeavingSite = this.siteConfigService.getGlobalConfig()
      ?.siteConfiguration?.siteParams?.leavingSite;
    if (customContent && labels) {
      // Converting kebab case from class to camel case for label names
      // Labels for custom content for leaving site modal naming convention:
      // <externalLinkClass suffix>Title, <externalLinkClass suffix>Content
      // Example: leavingSsoTitle, leavingSsoContent
      // Suffix for external-link class should be kebab case: leaving-sso
      const customContentCamelize = customContent.replace(
        /-./g,
        (x) => x.toUpperCase()[1]
      );
      this.dialogConfigCustom = this.setDialogConfig(
        labels[`${customContentCamelize}Title`],
        labels[`${customContentCamelize}Content`],
        labels
      );
      this.dialogConfigCustom.eventName = customContent;
    }
    if (
      leavingSite?.leavingSiteContent &&
      leavingSite?.leavingSiteTitle &&
      labels
    ) {
      this.dialogConfig = this.setDialogConfig(
        leavingSite?.leavingSiteTitle,
        leavingSite?.leavingSiteContent,
        labels
      );
      return;
    }
    // If Leaving Site is not configured in site config, then taking from common labels.
    if (labels) {
      this.dialogConfig = this.setDialogConfig(
        labels.leavingSiteTitle,
        labels.leavingSiteContent,
        labels
      );
    }
  }

  /**
   * Sets dialog config parameters
   * @param titleLabel - Modal title string
   * @param contentLabel - Modal content string
   * @param labels - labels for buttons
   */
  private setDialogConfig(
    titleLabel: string,
    contentLabel: string,
    labels: any
  ): DialogConfig {
    return {
      title: titleLabel,
      content: `<p>${contentLabel}</p>`,
      cancelBtnLabel: labels.cancel,
      closeBtnLabel: labels.close,
      okBtnLabel: labels.ok,
    };
  }

  /**
   * If OneTrust is loaded toggle settings
   */
  showCookieSettings() {
    if (this.windowRef.OneTrust != null) {
      this.windowRef.OneTrust.ToggleInfoDisplay();
    }
  }

  setMetaData(page: Page) {
    const metadata = get(page, ['model', 'page', 'models', 'pageData']);
    if (metadata) {
      if (metadata.title) {
        this.title.setTitle(
          metadata.title +
            (metadata.appendedTitleText
              ? ' | ' + metadata.appendedTitleText
              : '')
        );
      }
      this.updateMetaTags([
        { name: 'description', content: metadata.description },
        { name: 'keywords', content: metadata.keywords },
        { name: 'language', content: metadata.language },
        {
          property: 'og:title',
          content:
            metadata.socialTitle && metadata.socialTitle !== ''
              ? metadata.socialTitle
              : metadata.title,
        },
        { property: 'og:site_name', content: metadata.siteName },
        {
          property: 'og:description',
          content:
            metadata.socialDescription && metadata.socialDescription !== ''
              ? metadata.socialDescription
              : metadata.description,
        },
        {
          property: 'og:image',
          content: this.widenService.getWidenImageVariantUrl(
            metadata.socialImage,
            'original',
            'jpeg'
          ),
        },
        { property: 'twitter:site', content: metadata.twitterSite },
        { property: 'twitter:card', content: metadata.twitterCard },
      ]);

      const robotsContent: string[] = [];
      robotsContent.push(metadata.noIndex ? 'noindex' : 'index');
      robotsContent.push(metadata.noFollow ? 'nofollow' : 'follow');
      this.meta.addTag({ name: 'robots', content: robotsContent.join(', ') });

      this.setCanonical(metadata.canonicalUrl);
      this.setFavicon(metadata.favicon);

      // Set lang attribute only if it exists
      if (metadata?.language && metadata?.language.trim().length > 1) {
        this.documentRef.documentElement.lang = metadata.language;
      }

      // Set default page title incase the above fails
      if (this.title.getTitle() === '') {
        this.title.setTitle('Franklin Templeton');
      }
    }
  }

  private updateMetaTags(tags: MetaDefinition[]): void {
    tags.forEach((tag) => {
      if (tag.content && tag.content !== '') {
        let selector: string;
        if (tag.property != null) {
          selector = `property='${tag.property}'`;
        } else {
          selector = `name='${tag.name}'`;
        }
        this.meta.updateTag(tag, selector);
      }
    });
  }

  private setCanonical(url: string): void {
    const value = url ? url : this.windowRef.location.href;
    let link: HTMLLinkElement =
      this.documentRef.querySelector(`link[rel='canonical']`) || null;
    if (!link) {
      link = this.documentRef.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.documentRef.head.appendChild(link);
    }
    link.setAttribute('href', value);
  }

  private setFavicon(url: string): void {
    if (url) {
      Array.from(this.documentRef.getElementsByTagName('link')).forEach(
        (link) => {
          if (link.rel === 'icon') {
            link.setAttribute('rel', 'shortcut icon');
            link.setAttribute('type', 'image/x-icon');
            link.setAttribute('href', url);
          }
        }
      );
    }
  }

  /**
   * Returns external custom content parameter for external modal
   * @param classList - DOMTokenList with anchor classes
   */
  private setExternalModalCustom(classList: DOMTokenList): string {
    return classList.value
      .split(' ')
      .find((css) => {
        return css.includes('external-link--');
      })
      .replace('external-link--', '');
  }

  /**
   * method removes parameter from URL
   * @param url - url string
   */
  removeParam(url: string, parameter: string) {
    return url
      .replace(new RegExp('[?&]' + parameter + '=[^&#]*(#.*)?$'), '$1')
      .replace(new RegExp('([?&])' + parameter + '=[^&]*&'), '$1');
  }
}
